import classNames from 'classnames';
import React, { Fragment, forwardRef } from 'react';

import { makeStyles } from '@mui/styles';

import { CandidateField } from '@vestahealthcare/common/enums';
import { translate } from '@vestahealthcare/common/i18n';
import { CareTeamCandidate } from '@vestahealthcare/common/models';
import { EMPTY } from '@vestahealthcare/common/utils/constants';
import { formatPhone, parse } from '@vestahealthcare/common/utils/phone';

import { Colors, CopyIcon, PanelInfo } from 'styleguide-v2';

import { capitalize } from 'lodash';

type Props = {
  candidate: CareTeamCandidate;
};

const useStyles = makeStyles({
  candidateContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '0.25rem 1rem',
  },
  splittedRow: {
    marginBottom: '0.125rem',
    width: 'calc(50% - 0.5rem)',
  },
  fullRow: {
    marginBottom: '0.125rem',
    minWidth: '100%',
  },
});

export const CandidateFieldsPanel = forwardRef<HTMLDivElement, Props>(
  ({ candidate }: Props, ref) => {
    const styles = useStyles();

    const formatCandidateField = (
      cf?: {
        field: CandidateField;
        value?: string;
      },
      className?: string,
    ) => {
      if (!cf) return <div className={classNames('no-margin', className)} />;

      const { field, value } = cf;
      const isBoolean = value && ['true', 'false'].includes(value);
      const isPhoneNumber = value && parse(value);
      const showCopy = value && !isBoolean;
      let fieldValue = field.toString();

      if (CandidateField.SUBTYPE_RELATIONSHIP_SPECIALITY === field)
        fieldValue = translate(
          'components.CandidateFieldsPanel.infoMin.SUBTYPE_RELATIONSHIP_SPECIALITY',
        );
      else if (CandidateField.PREFERRED_CONTACT === field)
        fieldValue = translate(
          'components.CandidateFieldsPanel.infoMin.PREFERRED_CONTACT',
        );
      else if (CandidateField.HCP === field)
        fieldValue = translate('components.CandidateFieldsPanel.infoMin.HCP');

      const parsedValue = /[A-Z_]+/.test(value || '')
        ? capitalize(value?.replaceAll('_', ' '))
        : value;

      return (
        <div className={classNames('no-margin', className)} key={fieldValue}>
          <span className="semi-bold black size-s">{fieldValue}: </span>
          {isBoolean && translate(`global.${value === 'true' ? 'yes' : 'no'}`)}
          {isPhoneNumber && formatPhone(value)}
          {!isBoolean && !isPhoneNumber && (parsedValue || EMPTY)}
          {showCopy && (
            <>
              {' '}
              <CopyIcon
                className="no-padding"
                value={isPhoneNumber ? formatPhone(value) : parsedValue}
                size="small"
                copyLabel={`Copy ${fieldValue}`}
              />
            </>
          )}
        </div>
      );
    };

    const getField = (
      fields: { field: CandidateField; value: string }[],
      searchField: CandidateField,
    ) => fields.find(({ field }) => field === searchField);

    const formatCandidateFields = (candidate: CareTeamCandidate) => {
      const phoneFields = candidate.candidateFieldPhones;
      const fieldTypes = (candidate.candidateFieldValuesSorted || []).map(
        ({ field }) => field,
      );
      const fields = candidate.candidateFieldValuesSorted || [];
      return (
        <>
          {phoneFields.map(({ number, type }, idx) => (
            <Fragment key={`candidate-phone-${number?.value || idx}`}>
              {formatCandidateField(number, styles.splittedRow)}
              {formatCandidateField(type, styles.splittedRow)}
            </Fragment>
          ))}
          {(fieldTypes.includes(CandidateField.TYPE_ROLE_TYPE) ||
            fieldTypes.includes(
              CandidateField.SUBTYPE_RELATIONSHIP_SPECIALITY,
            )) && (
            <>
              {formatCandidateField(
                getField(fields, CandidateField.TYPE_ROLE_TYPE) || {
                  field: CandidateField.TYPE_ROLE_TYPE,
                },
                styles.splittedRow,
              )}
              {formatCandidateField(
                getField(
                  fields,
                  CandidateField.SUBTYPE_RELATIONSHIP_SPECIALITY,
                ) || {
                  field: CandidateField.SUBTYPE_RELATIONSHIP_SPECIALITY,
                },
                styles.splittedRow,
              )}
            </>
          )}
          {fields
            .filter(
              ({ field }) =>
                ![
                  CandidateField.PHONE_NUMBER_1,
                  CandidateField.PHONE_NUMBER_2,
                  CandidateField.PHONE_NUMBER_3,
                  CandidateField.PHONE_TYPE_1,
                  CandidateField.PHONE_TYPE_2,
                  CandidateField.PHONE_TYPE_3,
                  CandidateField.TYPE_ROLE_TYPE,
                  CandidateField.SUBTYPE_RELATIONSHIP_SPECIALITY,
                ].includes(field),
            )
            .map((field) => formatCandidateField(field, styles.fullRow))}
        </>
      );
    };

    if (!candidate.candidateFieldValuesSorted?.length) return <></>;

    return (
      <PanelInfo className={styles.fullRow} type="info" icon={<></>} ref={ref}>
        <div className={styles.fullRow}>
          <p className="bold" style={{ color: Colors.textBlack }}>
            {translate('components.CandidateFieldsPanel.info')}:
          </p>
          <div className={styles.candidateContainer}>
            {formatCandidateFields(candidate)}
          </div>
        </div>
      </PanelInfo>
    );
  },
);
